import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { ResourceType } from 'modules/workflows';
import { NodeEditor } from 'modules/workflows/components/NodeEditor';
import {
  WorkflowsRootState,
  addEdges,
  deleteEdges,
  getDependentEdges,
  getIsNodeEditorReadOnly,
  updateNodeData,
} from 'modules/workflows/ducks';
import { useDisplayCloneErrors } from 'modules/workflows/hooks/useDisplayCloneErrors';
import { AttributeSplitNodeData, Edge } from 'modules/workflows/types';
import { ErrorBanner } from 'ui/banner';
import { CHECKBOX_STATUS, Checkbox } from 'ui/checkbox';
import { colors } from 'ui/theme';
import { HelpToggletip, TipPlacement } from 'ui/tooltip';
import { NodeId } from 'ui/workflows/flowchart';

import { FormHeading, FormSectionContent } from '../../EditorBody.styled';
import { AttributeSplitResourceFieldSelect } from '../../editorComponents/ResourceFieldSelect/ResourceFieldSelect';
import { useFieldMappingOptions } from '../../editorComponents/ResourceFieldSelect/useFieldMappingOptions';
import { AddSplits, SplitTypeaheadSelect } from '../../editorComponents/SplitEditors';
import {
  InvalidSplits,
  RetryLink,
  SplitList,
} from '../../editorComponents/SplitEditors/SplitEditor.style';
import { SplitEditor } from '../../editorComponents/SplitEditors/enum';
import {
  AttributeSplitEdgeRemapper,
  useAttributeSplitEdgeRemapper,
} from '../../editorComponents/utils/useEdgeRemapper';
import { EditorBaseProps } from '../../types';
import {
  CheckboxLabel,
  DefaultSplit,
  SplitInputWraper,
  StyledSplitInput,
} from './AttributeSplitEditor.styled';
import { MAX_SPLITS, MIN_SPLIT_VALUES, SPLIT_VALUE_PLACEHOLDERS } from './constants';
import { useFieldFacets } from './useFieldFacets';
import { useSplitValidation } from './useSplitValidation';
import { useSplitValues } from './useSplitValues';
import { getAttributeSplitEditorNodeDataToBeCompared } from './utils';

export const ATTRIBUTE_SPLIT_EDITOR_TEST_ID = 'attribute-split-editor';
export const ATTRIBUTE_SELECT_TEST_ID = 'attribute-select';
export const ATTRIBUTE_SPLIT_TOOLTIP = 'attribute-split-tooltip';
export const ATTRIBUTE_VALUES_ERROR_BANNER_TEST_ID = 'attribute-values-error-banner';
export const RETRY_FIELD_FACET_TEST_ID = 'attribute-values-retry-field-facets';
export const INVALID_SPLIT_TEST_ID = 'invalid-attribute-splits';
export const DEFAULT_SPLIT_TEST_ID = 'default-split-checkbox';
export const DEFAULT_SPLIT_TOOLTIP_TEST_ID = 'default-split-tooltip';

export type AttributeSplitEditorProps = EditorBaseProps<AttributeSplitNodeData>;

/**
 * Converts boolean to checkbox status
 */
const getHasDefaultSplitChecked = (hasDefaultSplit: boolean) =>
  hasDefaultSplit ? CHECKBOX_STATUS.CHECKED : CHECKBOX_STATUS.UNCHECKED;

/**
 * Referred to as field split in classic version instead of attribute split
 */
export const AttributeSplitEditor: FunctionComponent<
  React.PropsWithChildren<AttributeSplitEditorProps>
> = ({ nodeData, nodeId, clonedFromNodeData, ...rest }) => {
  const [field, setField] = useState(nodeData?.field);

  const [fieldOptions, isLoadingMapping] = useFieldMappingOptions({
    dataType: ResourceType.ProfileField,
    isStartNode: false,
  });
  const selectedFieldType = fieldOptions.find((option) => option.value === field)?.Suffix ?? '';

  const [fieldType, setFieldType] = useState('');

  useEffect(() => {
    setFieldType(selectedFieldType as string);
  }, [selectedFieldType]);

  const { facetOptions, hasFailed, isLoaded, refetch } = useFieldFacets(field || '', fieldType);
  const dispatch = useDispatch();

  const edges = useSelector((state) =>
    getDependentEdges(state as WorkflowsRootState, nodeId as NodeId),
  );

  const { splitValues, hasDefaultSplit, onAdd, onClearAll, onRemove, onUpdate, onDefaultSplit } =
    useSplitValues(nodeData?.splitValues);

  const [
    remappedEdges,
    { removeEdgeAt, shiftDefaultEdgeAt, removeDefaultEdgeAt },
  ]: AttributeSplitEdgeRemapper = useAttributeSplitEdgeRemapper(edges, hasDefaultSplit);

  const { splitValidation, isValid } = useSplitValidation(splitValues);

  const filteredSplitValues = splitValues.filter((split) => !split.isDefault); // filters out default split for display
  useEffect(() => {
    // require "everyone else" branch if we only have one split
    if (filteredSplitValues.length === 1) {
      onDefaultSplit(true);
    }
  }, [filteredSplitValues.length]);
  const onFieldChange = (option: { value: string; Suffix: string }) => {
    const updatedField = option.value;
    setField(updatedField);
    setFieldType(option.Suffix);
    onClearAll();
  };

  const handleRemove = (index: number) => {
    onRemove(index);
    removeEdgeAt(index);
  };

  const handleAdd = () => {
    onAdd();
    shiftDefaultEdgeAt(splitValues.length - 1);
  };

  const handleUpdate = (requiresValidation = true) => {
    if (!isValid && requiresValidation) return; // do not allow the node to be updated if it is invalid or in draft mode.
    const dataToUpdateInTheStore = {
      field,
      splitValues,
    } as any;

    dispatch(updateNodeData(dataToUpdateInTheStore, nodeId));
    dispatch(deleteEdges(edges));
    dispatch(addEdges(remappedEdges as Edge[]));
  };

  const toggleHasDefaultSplit = () => {
    onDefaultSplit(!hasDefaultSplit);
    removeDefaultEdgeAt(splitValues.length - 1);
  };

  const initialNodeData = useMemo(() => {
    const data = { field: nodeData?.field, splitValues: nodeData?.splitValues };
    return getAttributeSplitEditorNodeDataToBeCompared(data);
  }, [nodeData?.field, nodeData?.splitValues]);

  const updatedNodeData = useMemo(() => {
    const data = { field, splitValues };
    return getAttributeSplitEditorNodeDataToBeCompared(data);
  }, [field, splitValues]);

  const { isCloneWarningBannerShown, cloneErrors, cloneDisplayValues } = useDisplayCloneErrors({
    clonedFromNodeData,
    fieldsToValidate: {
      field: {
        isValid: field,
      },
    },
  });

  /** read only state related variables */
  const isTileEditorReadOnly = useSelector(getIsNodeEditorReadOnly);

  return (
    <NodeEditor
      {...rest}
      dataTest={ATTRIBUTE_SPLIT_EDITOR_TEST_ID}
      onUpdate={handleUpdate}
      /** These are used to check if node has changes */
      initialNodeData={initialNodeData}
      updatedNodeData={updatedNodeData}
      isCloneWarningBannerShown={isCloneWarningBannerShown}
    >
      <FormHeading>
        Attribute
        <HelpToggletip
          placement={TipPlacement.Right}
          tooltipContent="If splitting on an array, contacts will flow to the first matched split in the order below."
          iconProps={{
            color: colors.secondaryText,
            'data-test': ATTRIBUTE_SPLIT_TOOLTIP,
          }}
        />
      </FormHeading>
      <FormSectionContent>
        <AttributeSplitResourceFieldSelect
          id={ATTRIBUTE_SELECT_TEST_ID}
          isDisabled={isTileEditorReadOnly}
          dataType={ResourceType.ProfileField}
          placeholder="Example: Location"
          displayValue={cloneDisplayValues?.field}
          nodeData={nodeData}
          onChange={onFieldChange}
          isError={cloneErrors?.field}
          isLoadingMapping={isLoadingMapping}
          fieldOptions={fieldOptions}
        />
      </FormSectionContent>
      <FormHeading>Values</FormHeading>
      <FormSectionContent>
        {hasFailed && (
          <ErrorBanner data-test={ATTRIBUTE_VALUES_ERROR_BANNER_TEST_ID}>
            There was an error fetching values for this attribute.{' '}
            <RetryLink data-test={RETRY_FIELD_FACET_TEST_ID} onClick={refetch} tabIndex={0}>
              Try again.
            </RetryLink>
          </ErrorBanner>
        )}
        <SplitList>
          {filteredSplitValues.map((splitValue, i) => (
            <SplitTypeaheadSelect
              canRemove={filteredSplitValues.length > MIN_SPLIT_VALUES}
              hasFetchedOptions={isLoaded}
              isReadOnly={!field || isTileEditorReadOnly}
              itemIndex={i}
              key={i}
              onRemove={handleRemove}
              onSelect={(val) => onUpdate(val, i)}
              options={facetOptions}
              placeholder={`Example: ${SPLIT_VALUE_PLACEHOLDERS[i]}`}
              splitEditor={SplitEditor.ATTRIBUTE}
              value={splitValue.value}
              hasError={!splitValidation[splitValue.value]}
              field={field}
            />
          ))}
          {/* If default split is on, we show an 'Everyone else' branch
          representing users who don't fit the specified branches */}
          {hasDefaultSplit && (
            <SplitInputWraper>
              <StyledSplitInput
                isDisabled
                showRemove={false}
                value="Everyone else"
                itemIndex={filteredSplitValues.length}
                splitEditor={SplitEditor.ATTRIBUTE}
              />
            </SplitInputWraper>
          )}
        </SplitList>
        {!isValid && (
          <InvalidSplits data-test={INVALID_SPLIT_TEST_ID}>
            Each value must be unique.
          </InvalidSplits>
        )}
        {filteredSplitValues.length < MAX_SPLITS && !isTileEditorReadOnly && (
          <AddSplits
            text="Add another value"
            toolTipText={`You can add up to ${MAX_SPLITS} splits`}
            onClick={handleAdd}
            splitEditor={SplitEditor.ATTRIBUTE}
          />
        )}
      </FormSectionContent>
      <FormSectionContent>
        <DefaultSplit>
          <Checkbox
            id={DEFAULT_SPLIT_TEST_ID}
            status={getHasDefaultSplitChecked(hasDefaultSplit)}
            onClick={toggleHasDefaultSplit}
            data-test={DEFAULT_SPLIT_TEST_ID}
            isDisabled={filteredSplitValues.length === 1 || isTileEditorReadOnly}
          />
          <CheckboxLabel
            onClick={() => {
              if (filteredSplitValues.length > 1) toggleHasDefaultSplit();
            }}
          >
            Add an &quot;Everyone else&quot; branch
          </CheckboxLabel>
          <HelpToggletip
            tooltipContent="Check this box if you want users who don’t match any of the values to continue on in the journey. (If you don’t include this, users who don’t match any of the values will exit the journey.)"
            iconProps={{
              color: colors.secondaryText,
              'data-test': DEFAULT_SPLIT_TOOLTIP_TEST_ID,
            }}
          />
        </DefaultSplit>
      </FormSectionContent>
    </NodeEditor>
  );
};
